import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { viewportSectionClasses } from "@/components/marketing/Section";
import { Button } from "@/components/ui/Button";
import { AnimatedContainer } from "@/components/ui/motion";
import WaveText from "@/components/ui/wave-text";
import { BRAND_NAME } from "@/lib/brand";
import { cn } from "@/lib/cn";
import { appUrl } from "@/lib/env";
import { preserveSearch } from "@/lib/nav";
import { useDesktopPlatform } from "@/lib/useDesktopPlatform";

/** Launch-only closing action. Kept separate so the waitlist CTA cannot drift. */
export function LaunchFinalCta() {
  const { t } = useTranslation();
  const showDesktopDownload = useDesktopPlatform() !== "unknown";
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get("ref");

  return (
    <section className={cn("relative overflow-hidden py-16 lg:py-20", viewportSectionClasses)}>
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-radial-glow opacity-80" />

      <AnimatedContainer className="relative z-0 -mb-[0.3em] px-2 pt-2 md:-mb-[0.48em]" delay={0.1}>
        <div
          aria-hidden
          className="watermark-text pointer-events-none select-none whitespace-nowrap text-center text-[clamp(5rem,17vw,15rem)] font-extrabold leading-[0.9] tracking-[-0.05em]"
        >
          {BRAND_NAME}
        </div>
      </AnimatedContainer>

      <AnimatedContainer className="relative z-[1] mx-auto flex max-w-3xl flex-col items-center px-5 text-center sm:px-6">
        <span className="mb-5 inline-flex w-fit items-center gap-2 font-sans text-eyebrow font-medium uppercase text-primary">
          <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
          {t("launchFinalCta.eyebrow")}
        </span>
        <h2 className="font-sans text-display font-semibold tracking-tight">
          <span className="block text-foreground/55">
            <WaveText text={t("launchFinalCta.titleLead")} />
          </span>
          <span className="block text-foreground">
            <WaveText text={t("launchFinalCta.titlePunch")} delay={0.55} />{" "}
            <span className="text-primary">
              <WaveText text={t("launchFinalCta.titleAccent")} delay={0.85} />
            </span>
          </span>
        </h2>
        <Button
          variant="primary"
          size="lg"
          href={appUrl("/sign-in", referralCode)}
          className="mt-9 h-auto min-h-12 w-full max-w-sm whitespace-normal py-3 text-center sm:w-auto"
          trailing={<ArrowRight className="h-4 w-4" aria-hidden />}
        >
          {t("launchFinalCta.action")}
        </Button>
        {showDesktopDownload ? (
          <Button
            variant="secondary"
            to={preserveSearch("/download", searchParams.toString())}
            className="mt-3 h-auto min-h-11 w-full max-w-sm whitespace-normal py-3 text-center sm:w-auto"
          >
            {t("nav.downloadDesktop")}
          </Button>
        ) : null}
      </AnimatedContainer>
    </section>
  );
}
